import { Tag, Globe, Languages, Calendar, Link as LinkIcon, Hash, Copy, Info } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

/**
 * "Channel Intelligence" — surfaces the metadata other creators use to model
 * a reference channel: keywords, topic categories, country, language, age,
 * custom URL. Everything rendered here comes from the YouTube Data API.
 */
export default function ChannelIntelligence({ stats }) {
  if (!stats) return null;

  const keywords = parseKeywords(stats.keywords);
  const topics = (stats.topic_categories || [])
    .map((t) => t.split("/").pop().replace(/_/g, " "))
    .filter(Boolean);
  const age = computeAge(stats.published_at);
  const uploadsPerYear = estimateUploadsPerYear(stats.avg_gap_days);

  const copyKeywords = async () => {
    if (!keywords.length) return;
    try {
      await navigator.clipboard.writeText(keywords.join(", "));
      toast.success(`Copied ${keywords.length} keywords`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const meta = [
    stats.country && { icon: Globe, label: "Country", value: stats.country, testId: "intel-country" },
    stats.default_language && { icon: Languages, label: "Language", value: stats.default_language, testId: "intel-language" },
    stats.published_at && { icon: Calendar, label: "Channel age", value: age, testId: "intel-age" },
    stats.custom_url && {
      icon: LinkIcon, label: "Handle", value: stats.custom_url, testId: "intel-handle",
      href: `https://youtube.com/${stats.custom_url}`,
    },
    uploadsPerYear !== null && {
      icon: Hash, label: "Est. uploads / year", value: uploadsPerYear, testId: "intel-uploads-year",
    },
  ].filter(Boolean);

  const hasContent = keywords.length > 0 || topics.length > 0 || meta.length > 0;
  if (!hasContent) return null;

  return (
    <section
      data-testid="channel-intelligence"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/40 flex items-center justify-center">
          <Info className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-3xl font-black tracking-tight">Channel intelligence</h2>
          <div className="overline mt-1">Reference data other creators use to model this channel</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Meta grid */}
        {meta.length > 0 && (
          <div data-testid="intel-meta" className="md:col-span-12 card-flat p-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {meta.map((m) => (
                <MetaCell key={m.label} {...m} />
              ))}
            </div>
          </div>
        )}

        {/* Keywords */}
        {keywords.length > 0 && (
          <div data-testid="intel-keywords" className="md:col-span-8 card-flat p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-primary" />
                <div className="overline">Channel keywords</div>
              </div>
              <button
                data-testid="intel-copy-keywords"
                onClick={copyKeywords}
                className="text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> Copy all
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {keywords.map((k) => (
                <span
                  key={k}
                  className="text-[12px] font-mono px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/20"
                >
                  {k}
                </span>
              ))}
            </div>
            <p className="mt-4 text-[12px] text-muted-foreground leading-relaxed">
              These are the exact keywords this channel declared to YouTube's algorithm. If you want to be discovered alongside this creator, mirror the ones that fit your niche in your own channel settings and video tags.
            </p>
          </div>
        )}

        {/* Topics */}
        {topics.length > 0 && (
          <div data-testid="intel-topics" className="md:col-span-4 card-flat p-6">
            <div className="flex items-center gap-2 mb-3">
              <Hash className="w-4 h-4 text-primary" />
              <div className="overline">Topic categories</div>
            </div>
            <ul className="space-y-1.5">
              {topics.map((t) => (
                <li key={t} className="text-sm capitalize">
                  <span className="text-primary mr-2">·</span>{t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[11px] text-muted-foreground leading-relaxed">
              YouTube assigns these based on channel content. Use them to identify the audience overlap with your niche.
            </p>
          </div>
        )}

        {/* Description excerpt */}
        {stats.description && (
          <div data-testid="intel-description" className="md:col-span-12 card-flat p-6">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <div className="overline">About (channel description)</div>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-line text-foreground/90">
              {stats.description}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function MetaCell({ icon: Icon, label, value, href, testId }) {
  const body = (
    <div className="flex items-start gap-2.5" data-testid={testId}>
      <Icon className="w-4 h-4 text-primary/80 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="overline text-[10px]">{label}</div>
        <div className="mt-0.5 text-sm font-semibold truncate">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
        {body}
      </a>
    );
  }
  return body;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse the raw YouTube channel `keywords` string into an array. YouTube
 * separates keywords with spaces but wraps multi-word ones in double quotes.
 */
function parseKeywords(raw) {
  if (!raw || typeof raw !== "string") return [];
  const tokens = [];
  let buffer = "";
  let inQuote = false;
  for (const ch of raw) {
    if (ch === '"') {
      if (inQuote && buffer) {
        tokens.push(buffer);
        buffer = "";
      }
      inQuote = !inQuote;
      continue;
    }
    if (!inQuote && ch === " ") {
      if (buffer) tokens.push(buffer);
      buffer = "";
      continue;
    }
    buffer += ch;
  }
  if (buffer) tokens.push(buffer);
  // Deduplicate while preserving order, cap at 40 to keep the UI clean.
  const seen = new Set();
  return tokens
    .map((t) => t.trim())
    .filter((t) => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()))
    .slice(0, 40);
}

function computeAge(publishedAt) {
  if (!publishedAt) return null;
  try {
    const start = new Date(publishedAt);
    const now = new Date();
    const years = (now - start) / (1000 * 60 * 60 * 24 * 365.25);
    if (years < 1) {
      const months = Math.round(years * 12);
      return `${months} month${months === 1 ? "" : "s"} · ${formatDate(publishedAt)}`;
    }
    const y = Math.floor(years);
    const m = Math.round((years - y) * 12);
    if (!m) return `${y} year${y === 1 ? "" : "s"} · ${formatDate(publishedAt)}`;
    return `${y}y ${m}m · ${formatDate(publishedAt)}`;
  } catch {
    return formatDate(publishedAt);
  }
}

function estimateUploadsPerYear(avgGapDays) {
  if (!avgGapDays || avgGapDays <= 0) return null;
  return Math.round(365 / avgGapDays);
}
